/**
 * 统一的图标与"未就绪 / 出错"状态块。
 *
 * 关于图标：儿童模式要求"图标+文字并列"（见方案 2.5），但孩子的任务名是家长
 * 自由填写的，不可能配上贴切的图标。这里的做法是**按关键词猜一个 emoji**，
 * 猜不到就用学科默认图标 —— 比清一色的 📘 更有辨识度，又不需要家长做额外配置。
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { EmptyState } from './Card';
import { Button } from './Button';

// ---------------------------------------------------------------- 图标

const KEYWORD_ICONS: Array<{ match: RegExp; icon: string }> = [
  { match: /阅读|看书|读书|绘本|朗读/, icon: '📖' },
  { match: /数学|算术|口算|计算|题/, icon: '🧮' },
  { match: /英语|单词|拼读|听力/, icon: '🔤' },
  { match: /语文|作文|写字|练字|生字/, icon: '✍️' },
  { match: /钢琴|练琴|乐器|小提琴|古筝/, icon: '🎹' },
  { match: /运动|跑步|跳绳|锻炼|体育|游泳|球/, icon: '🏃' },
  { match: /画|美术|手工/, icon: '🎨' },
  { match: /编程|代码|机器人/, icon: '💻' },
  { match: /作业/, icon: '📝' },
  { match: /复习|预习/, icon: '🔁' },
  { match: /背|背诵|古诗|课文/, icon: '🧠' },
  { match: /家务|整理|打扫|收拾/, icon: '🧹' },
  { match: /刷牙|洗脸|作息|睡觉|早起/, icon: '🪥' },
  { match: /钢琴|音乐|唱歌|合唱/, icon: '🎵' },
];

/** 按名称猜图标；猜不到用默认值。 */
export function iconForName(name: string, fallback = '📘'): string {
  for (const entry of KEYWORD_ICONS) {
    if (entry.match.test(name)) return entry.icon;
  }
  return fallback;
}

/** 任务状态的统一展示（emoji + 中文），避免各页面各写一套。 */
export const STATUS_META: Record<
  string,
  { label: string; icon: string; tone: 'neutral' | 'brand' | 'success' | 'warn' | 'danger' }
> = {
  pending: { label: '待开始', icon: '⏳', tone: 'neutral' },
  running: { label: '计时中', icon: '⏱️', tone: 'brand' },
  paused: { label: '已暂停', icon: '⏸️', tone: 'warn' },
  auto_paused: { label: '自动暂停', icon: '😴', tone: 'warn' },
  done: { label: '已完成', icon: '✅', tone: 'success' },
  /**
   * ⚠️ `submitted` 必须在这里有自己的一行，**不能**靠 `statusMeta` 的兜底。
   * 它是作业流程的关键中间态（孩子点完成 → 待家长验收 → 通过才计分）：
   * 兜底会渲染成 "• submitted"，孩子会以为"我打完了"，家长会以为"还没交"。
   */
  submitted: { label: '待家长验收', icon: '📤', tone: 'warn' },
  missed: { label: '未完成', icon: '❌', tone: 'danger' },
  skipped: { label: '已放弃', icon: '🚫', tone: 'danger' },
};

export function statusMeta(status: string): { label: string; icon: string; tone: 'neutral' | 'brand' | 'success' | 'warn' | 'danger' } {
  return STATUS_META[status] ?? { label: status, icon: '•', tone: 'neutral' };
}

// ---------------------------------------------------------------- 状态块

/**
 * "该功能尚未就绪"。
 *
 * 本批页面全部依赖正在并行实现的后端模块，所以每个页面都必须能优雅降级 ——
 * 接口 404/501 时显示这个块，而不是白屏或抛未捕获异常（见任务说明）。
 */
export function NotReadyState({
  feature,
  description,
  compact = false,
}: {
  feature: string;
  description?: string;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-4 py-3 text-sm text-amber-800">
        <span aria-hidden="true" className="mr-1">
          🚧
        </span>
        {feature}尚未就绪，稍后刷新即可。
      </div>
    );
  }
  return (
    <EmptyState
      icon="🚧"
      title={`${feature}尚未就绪`}
      description={
        description ??
        '这一部分的接口还在上线中。你现在看到的是界面本身，数据接通后会自动出现，不需要重新设置。'
      }
    />
  );
}

/** 加载失败：把后端返回的中文 message 直接展示（见契约 4）。 */
export function ErrorState({
  message,
  onRetry,
  compact = false,
}: {
  message: string;
  onRetry?: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        <span>{message}</span>
        {onRetry && (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            重试
          </Button>
        )}
      </div>
    );
  }
  return (
    <EmptyState
      icon="⚠️"
      title="没能加载出来"
      description={message}
      action={
        onRetry ? (
          <Button size="sm" variant="secondary" className="mt-2" onClick={onRetry}>
            重试
          </Button>
        ) : undefined
      }
    />
  );
}

// ---------------------------------------------------------------- 进度

/** 进度条。`ratio` 为 null 时显示"长期 · 只累计不显示百分比"（见方案 1.3 ⑭）。 */
export function ProgressBar({
  ratio,
  done,
  target,
  unit,
  kid = false,
  className,
}: {
  ratio: number | null;
  done?: number;
  target?: number;
  unit?: string | null;
  kid?: boolean;
  className?: string;
}) {
  const label =
    ratio === null
      ? done !== undefined
        ? `已累计 ${done}${unit ?? ''}`
        : '长期目标'
      : `${done ?? 0} / ${target ?? 0}${unit ?? ''} · ${Math.round(ratio * 100)}%`;

  return (
    <div className={cn('space-y-1', className)}>
      <div
        className={cn('w-full overflow-hidden rounded-full bg-slate-100', kid ? 'h-4' : 'h-2')}
        role="progressbar"
        aria-valuenow={ratio === null ? undefined : Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className="h-full rounded-full bg-brand-500 transition-[width]"
          style={{ width: `${Math.round(Math.min(1, Math.max(0, ratio ?? 0)) * 100)}%` }}
        />
      </div>
      <p className={cn('text-slate-500', kid ? 'text-base' : 'text-xs')}>{label}</p>
    </div>
  );
}

/** 大数字统计卡（仪表盘与家长端首页共用）。 */
export function StatTile({
  label,
  value,
  hint,
  tone = 'brand',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'brand' | 'amber' | 'emerald' | 'slate';
}) {
  const tones = {
    brand: 'bg-brand-50 text-brand-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    slate: 'bg-slate-100 text-slate-700',
  } as const;
  return (
    <div className={cn('rounded-2xl px-4 py-3', tones[tone])}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-xs opacity-70">{hint}</p>}
    </div>
  );
}
